/**
 * Modal para ver y editar una Orden de Compra completa
 * Muestra datos de OrdenCompra y todos sus DetalleOrdenCompra
 */

import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { toast } from 'react-toastify';
import { ordenCompraService, detalleOrdenCompraService, proveedorService, presupuestoCostoService } from '../services/onebaseService';
import presupuestoHierarchyService from '../services/presupuestoHierarchyService';
import { FiX, FiSave, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './CrearModal.css';

const VerEditarOrdenCompraModal = ({
  ordenCompra,
  onClose,
  onUpdated,
  readOnly = true
}) => {
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(!readOnly);
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    numero_oc: '',
    proveedor: null,
    descripcion: '',
    fecha_emision: '',
    estado: '',
    tipo_documento: '',
    observaciones: ''
  });
  const [detalles, setDetalles] = useState([]);
  const [detallesEditando, setDetallesEditando] = useState({});
  const [mostrarAgregarRecurso, setMostrarAgregarRecurso] = useState(false);
  const [nuevoRecurso, setNuevoRecurso] = useState({
    recurso_presupuesto_costo: null,
    cantidad: '1',
    precio_unitario: '0'
  });
  const [recursosDisponibles, setRecursosDisponibles] = useState([]);

  useEffect(() => {
    cargarDatos();
  }, [ordenCompra]);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      console.log('📋 Cargando datos de OC:', ordenCompra);

      const proveedoresResponse = await proveedorService.getAll({ status: 'activo' });
      let proveedoresData = proveedoresResponse.data?.results || proveedoresResponse.data || [];
      setProveedores(proveedoresData);

      if (ordenCompra) {
        console.log('🔍 Presupuesto de costo en OC:', ordenCompra.presupuesto_costo);
        setFormData({
          numero_oc: ordenCompra.numero_oc || '',
          proveedor: ordenCompra.proveedor,
          descripcion: ordenCompra.descripcion || '',
          fecha_emision: ordenCompra.fecha_emision || '',
          estado: ordenCompra.estado || '',
          tipo_documento: ordenCompra.tipo_documento || '',
          observaciones: ordenCompra.observaciones || ''
        });

        // Cargar los detalles de la OC desde la API
        try {
          const detallesResponse = await detalleOrdenCompraService.getAll({
            orden_compra: ordenCompra.id,
            status: 'activo'
          });
          let detallesData = detallesResponse.data?.results || detallesResponse.data || [];
          setDetalles(detallesData);

          // Obtener el presupuesto de costo del proyecto
          let presupuestoCostoId = null;

          try {
            // Si la OC tiene presupuesto_costo, usarlo
            if (ordenCompra.presupuesto_costo) {
              presupuestoCostoId = ordenCompra.presupuesto_costo;
              console.log('✅ Presupuesto encontrado en OC:', presupuestoCostoId);
            } else {
              // Si no, obtener todos los presupuestos de costo del proyecto y usar el primero
              console.log('📦 Obteniendo presupuestos del proyecto:', ordenCompra.proyecto);
              const presupuestosResponse = await presupuestoCostoService.getAll({
                proyecto: ordenCompra.proyecto,
                status: 'activo'
              });

              const presupuestos = presupuestosResponse.data?.results || presupuestosResponse.data || [];
              console.log('📋 Presupuestos encontrados:', presupuestos.length);

              if (presupuestos.length > 0) {
                presupuestoCostoId = presupuestos[0].id;
                console.log('✅ Usando presupuesto del proyecto:', presupuestoCostoId);
              }
            }

            // Cargar recursos disponibles del presupuesto de costo
            if (presupuestoCostoId) {
              try {
                console.log('🔄 Cargando áreas del presupuesto de costo:', presupuestoCostoId);

                // Obtener todas las áreas del presupuesto de costo
                const areasResponse = await presupuestoHierarchyService.getCostoAreasForPresupuesto(presupuestoCostoId);
                const areas = areasResponse.data?.results || areasResponse.data || [];

                console.log('📊 Áreas obtenidas:', areas.length);

                // Extraer todos los recursos de la jerarquía de áreas
                let todosRecursos = [];
                areas.forEach(area => {
                  console.log('📍 Procesando área:', area.nombre, 'con', area.partidas_estructura?.length || 0, 'partidas');
                  if (area.partidas_estructura && Array.isArray(area.partidas_estructura)) {
                    area.partidas_estructura.forEach(partida => {
                      if (partida.recursos_estructura && Array.isArray(partida.recursos_estructura)) {
                        console.log('  📦 Partida:', partida.nombre, 'con', partida.recursos_estructura.length, 'recursos');
                        todosRecursos = [...todosRecursos, ...partida.recursos_estructura];
                      }
                    });
                  }
                });

                console.log('📌 Total recursos en presupuesto:', todosRecursos.length);

                // Filtrar recursos que no estén ya en la OC y que tengan saldo disponible
                const recursosEnOC = new Set(detallesData.map(d => d.recurso_presupuesto_costo));
                const recursosDisponibles = todosRecursos.filter(r => {
                  const yaEnOC = recursosEnOC.has(r.id);
                  const cantidadDisponible = r.cantidad_presupuesto - r.cantidad_solicitada;
                  const tieneDisponible = cantidadDisponible > 0;

                  if (yaEnOC) {
                    console.log(`⏭️  Recurso ${r.nombre} ya está en OC, descartado`);
                  }
                  if (!tieneDisponible) {
                    console.log(`❌ Recurso ${r.nombre} sin saldo disponible (disponible: ${cantidadDisponible}), descartado`);
                  }

                  return !yaEnOC && tieneDisponible;
                });

                console.log('✨ Recursos disponibles cargados:', recursosDisponibles.length);
                setRecursosDisponibles(recursosDisponibles);
              } catch (error) {
                console.error('❌ Error al cargar recursos disponibles:', error);
                setRecursosDisponibles([]);
              }
            } else {
              console.log('⚠️ No hay presupuesto_costo disponible en el proyecto');
              setRecursosDisponibles([]);
            }
          } catch (error) {
            console.error('❌ Error al obtener presupuesto del proyecto:', error);
            setRecursosDisponibles([]);
          }
        } catch (error) {
          console.error('❌ Error al cargar detalles:', error);
          // Si falla, intentar usar los detalles que vienen en ordenCompra
          if (ordenCompra.detalles && Array.isArray(ordenCompra.detalles)) {
            setDetalles(ordenCompra.detalles);
          }
        }
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar la orden de compra');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDetalleChange = (detalleId, field, value) => {
    setDetallesEditando(prev => ({
      ...prev,
      [detalleId]: {
        ...prev[detalleId],
        [field]: value
      }
    }));
  };

  const handleGuardarDetalle = async (detalle) => {
    try {
      const cambios = detallesEditando[detalle.id];
      if (cambios) {
        const detalleData = {
          cantidad: cambios.cantidad !== undefined ? cambios.cantidad : detalle.cantidad,
          precio_unitario: cambios.precio_unitario !== undefined ? cambios.precio_unitario : detalle.precio_unitario,
          descripcion: cambios.descripcion !== undefined ? cambios.descripcion : detalle.descripcion
        };

        await detalleOrdenCompraService.update(detalle.id, detalleData);
        toast.success('Detalle actualizado correctamente');

        // Actualizar el detalle en la lista
        setDetalles(prev => prev.map(d =>
          d.id === detalle.id ? { ...d, ...detalleData } : d
        ));

        // Limpiar estado de edición
        setDetallesEditando(prev => {
          const newState = { ...prev };
          delete newState[detalle.id];
          return newState;
        });
      }
    } catch (error) {
      console.error('Error al actualizar detalle:', error);
      toast.error('Error al actualizar detalle');
    }
  };

  const handleEliminarDetalle = async (detalleId) => {
    if (!window.confirm('¿Está seguro de que desea eliminar este recurso de la orden?')) {
      return;
    }

    try {
      await detalleOrdenCompraService.delete(detalleId);
      toast.success('Recurso eliminado correctamente');
      setDetalles(prev => prev.filter(d => d.id !== detalleId));

      // Limpiar estado de edición
      setDetallesEditando(prev => {
        const newState = { ...prev };
        delete newState[detalleId];
        return newState;
      });
    } catch (error) {
      console.error('Error al eliminar detalle:', error);
      toast.error('Error al eliminar recurso');
    }
  };

  const handleAgregarRecurso = async () => {
    if (!nuevoRecurso.recurso_presupuesto_costo || !nuevoRecurso.cantidad || !nuevoRecurso.precio_unitario) {
      toast.error('Por favor complete todos los campos');
      return;
    }

    // Validar cantidad disponible
    const recursoSeleccionado = recursosDisponibles.find(r => r.id === parseInt(nuevoRecurso.recurso_presupuesto_costo));
    if (recursoSeleccionado) {
      const cantidadDisponible = recursoSeleccionado.cantidad_presupuesto - recursoSeleccionado.cantidad_solicitada;
      const cantidadSolicitada = parseFloat(nuevoRecurso.cantidad);

      if (cantidadSolicitada > cantidadDisponible) {
        toast.error(
          `Cantidad disponible insuficiente. ` +
          `Presupuestada: ${recursoSeleccionado.cantidad_presupuesto}, ` +
          `Solicitada: ${recursoSeleccionado.cantidad_solicitada}, ` +
          `Disponible: ${cantidadDisponible}`
        );
        return;
      }
    }

    try {
      const detalleData = {
        orden_compra: ordenCompra.id,
        recurso_presupuesto_costo: nuevoRecurso.recurso_presupuesto_costo,
        cantidad: nuevoRecurso.cantidad,
        precio_unitario: nuevoRecurso.precio_unitario,
        descripcion: `Detalle OC ${ordenCompra.numero_oc}`
      };

      const response = await detalleOrdenCompraService.create(detalleData);
      toast.success('Recurso agregado correctamente');

      // Agregar el nuevo detalle a la lista con la data del recurso
      const nuevoDetalle = response.data;
      setDetalles(prev => [...prev, nuevoDetalle]);

      // Actualizar recursos disponibles
      setRecursosDisponibles(prev =>
        prev.filter(r => r.id !== nuevoRecurso.recurso_presupuesto_costo)
      );

      // Resetear formulario
      setNuevoRecurso({
        recurso_presupuesto_costo: null,
        cantidad: '1',
        precio_unitario: '0'
      });
      setMostrarAgregarRecurso(false);
    } catch (error) {
      console.error('Error al agregar recurso:', error);
      toast.error('Error al agregar recurso');
    }
  };

  const calcularTotal = () => {
    return detalles.reduce((total, detalle) => {
      return total + (parseFloat(detalle.cantidad || 0) * parseFloat(detalle.precio_unitario || 0));
    }, 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Parse date as YYYY-MM-DD to avoid timezone offset issues
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day).toLocaleDateString('es-CL');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const ocData = {
        proveedor: formData.proveedor,
        descripcion: formData.descripcion,
        fecha_emision: formData.fecha_emision,
        estado: formData.estado,
        tipo_documento: formData.tipo_documento,
        observaciones: formData.observaciones
      };

      await ordenCompraService.update(ordenCompra.id, ocData);
      toast.success('Orden de compra actualizada correctamente');
      setEditMode(false);
      if (onUpdated) {
        onUpdated();
      }
    } catch (error) {
      console.error('Error al actualizar:', error);
      toast.error('Error al actualizar la orden de compra');
    } finally {
      setLoading(false);
    }
  };

  if (!ordenCompra) return null;

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <h2>Orden de Compra #{ordenCompra.numero_oc}</h2>
          <button className="close-btn" onClick={onClose}>
            <FiX />
          </button>
        </div>

        {editMode ? (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-section">
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px' }}>
                <div className="form-group">
                  <label>Proveedor *</label>
                  <select
                    name="proveedor"
                    value={formData.proveedor || ''}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Seleccionar proveedor</option>
                    {proveedores.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Fecha de Emisión *</label>
                  <input
                    type="date"
                    name="fecha_emision"
                    value={formData.fecha_emision}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Estado</label>
                  <select
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                  >
                    <option value="pendiente">Pendiente</option>
                    <option value="emitida">Emitida</option>
                    <option value="recibida">Recibida</option>
                    <option value="facturada">Facturada</option>
                    <option value="cancelada">Cancelada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Tipo de Documento</label>
                  <select
                    name="tipo_documento"
                    value={formData.tipo_documento}
                    onChange={handleInputChange}
                  >
                    <option value="">Seleccionar</option>
                    <option value="FE">Factura Electrónica</option>
                    <option value="BHE">Boleta de Honorarios</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Descripción</label>
                <textarea
                  name="descripcion"
                  value={formData.descripcion}
                  onChange={handleInputChange}
                  rows="3"
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Recursos de la Orden</h3>
              <table className="detalles-table" style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                    color: 'white'
                  }}>
                    <th style={{ textAlign: 'left', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Recurso</th>
                    <th style={{ textAlign: 'center', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Cantidad</th>
                    <th style={{ textAlign: 'right', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Precio Unitario</th>
                    <th style={{ textAlign: 'right', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Subtotal</th>
                    <th style={{ textAlign: 'center', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((detalle, index) => {
                    const isEditing = detallesEditando[detalle.id];
                    const cantidad = isEditing?.cantidad !== undefined ? isEditing.cantidad : detalle.cantidad;
                    const precio = isEditing?.precio_unitario !== undefined ? isEditing.precio_unitario : detalle.precio_unitario;

                    return (
                      <tr
                        key={detalle.id}
                        style={{
                          borderBottom: '1px solid #e5e7eb',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                      >
                        <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                          <div style={{ fontWeight: 600, color: '#1f2937' }}>
                            {detalle.recurso_data?.nombre || detalle.descripcion}
                          </div>
                          {/* Mostrar cantidades si tiene recurso_presupuesto_costo asociado */}
                          {detalle.recurso_data && detalle.recurso_presupuesto_costo && (
                            <div style={{
                              marginTop: '6px',
                              fontSize: '0.75rem',
                              color: '#6b7280'
                            }}>
                              {/* Primera línea: Presupuestada y Disponible */}
                              <div style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                marginBottom: '3px'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <strong style={{ color: '#3b82f6' }}>Presupuestada:</strong>
                                  {detalle.recurso_data.cantidad_presupuesto?.toLocaleString('es-CL') || 0}
                                </span>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                  <strong style={{ color: detalle.recurso_data.cantidad_disponible < 0 ? '#ef4444' : '#10b981' }}>
                                    Disponible:
                                  </strong>
                                  <span style={{ color: detalle.recurso_data.cantidad_disponible < 0 ? '#ef4444' : '#10b981' }}>
                                    {detalle.recurso_data.cantidad_disponible?.toLocaleString('es-CL') || 0}
                                  </span>
                                </span>
                              </div>
                              {/* Segunda línea: Desglose de solicitado */}
                              <div style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                                fontSize: '0.7rem',
                                color: '#9ca3af'
                              }}>
                                <span>
                                  <strong style={{ color: '#f59e0b' }}>Solicitada:</strong> {detalle.recurso_data.cantidad_solicitada?.toLocaleString('es-CL') || 0}
                                </span>
                                <span style={{ color: '#d1d5db' }}>|</span>
                                <span>
                                  OC: {detalle.recurso_data.cantidad_solicitada_oc?.toLocaleString('es-CL') || 0}
                                </span>
                                <span style={{ color: '#d1d5db' }}>+</span>
                                <span>
                                  Salidas: {detalle.recurso_data.cantidad_salidas_bodega?.toLocaleString('es-CL') || 0}
                                </span>
                              </div>
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center', padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              value={cantidad}
                              onChange={(e) => handleDetalleChange(detalle.id, 'cantidad', e.target.value)}
                              style={{ width: '80px', padding: '4px' }}
                            />
                          ) : (
                            parseFloat(detalle.cantidad).toLocaleString('es-CL')
                          )}
                        </td>
                        <td style={{ textAlign: 'right', padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                          {isEditing ? (
                            <input
                              type="number"
                              step="0.01"
                              value={precio}
                              onChange={(e) => handleDetalleChange(detalle.id, 'precio_unitario', e.target.value)}
                              style={{ width: '120px', padding: '4px', textAlign: 'right' }}
                            />
                          ) : (
                            `$${parseFloat(detalle.precio_unitario).toLocaleString('es-CL', { minimumFractionDigits: 2 })}`
                          )}
                        </td>
                        <td style={{
                          textAlign: 'right',
                          padding: '14px',
                          fontSize: '0.95rem',
                          fontWeight: 700,
                          color: '#ef4444'
                        }}>
                          ${(parseFloat(cantidad) * parseFloat(precio)).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                        </td>
                        <td style={{ textAlign: 'center', padding: '14px' }}>
                          {isEditing ? (
                            <>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleGuardarDetalle(detalle)}
                                title="Guardar"
                                style={{ marginRight: '5px', color: '#4CAF50' }}
                              >
                                <FiSave size={16} />
                              </button>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => {
                                  setDetallesEditando(prev => {
                                    const newState = { ...prev };
                                    delete newState[detalle.id];
                                    return newState;
                                  });
                                }}
                                title="Cancelar"
                                style={{ color: '#757575' }}
                              >
                                ✕
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleDetalleChange(detalle.id, 'cantidad', detalle.cantidad)}
                                title="Editar"
                                style={{ marginRight: '5px', color: '#2196F3' }}
                              >
                                <FiEdit2 size={16} />
                              </button>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => handleEliminarDetalle(detalle.id)}
                                title="Eliminar"
                                style={{ color: '#f44336' }}
                              >
                                <FiTrash2 size={16} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {mostrarAgregarRecurso ? (
                <div style={{ marginTop: '15px', padding: '15px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  <h4>Agregar Recurso a la Orden</h4>

                  {/* Selector de Recurso */}
                  <div style={{ marginBottom: '15px' }}>
                    <label>Recurso *</label>
                    <select
                      value={nuevoRecurso.recurso_presupuesto_costo || ''}
                      onChange={(e) => setNuevoRecurso(prev => ({ ...prev, recurso_presupuesto_costo: e.target.value ? parseInt(e.target.value) : null }))}
                      style={{ width: '100%', padding: '8px', marginBottom: '10px' }}
                    >
                      <option value="">Seleccionar recurso</option>
                      {recursosDisponibles.map(r => (
                        <option key={r.id} value={r.id}>
                          {r.nombre}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Información de Cantidades */}
                  {nuevoRecurso.recurso_presupuesto_costo && recursosDisponibles.find(r => r.id === parseInt(nuevoRecurso.recurso_presupuesto_costo)) && (() => {
                    const recursoSeleccionado = recursosDisponibles.find(r => r.id === parseInt(nuevoRecurso.recurso_presupuesto_costo));
                    const cantidadDisponible = recursoSeleccionado.cantidad_presupuesto - recursoSeleccionado.cantidad_solicitada;
                    return (
                      <div style={{
                        padding: '10px',
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        marginBottom: '15px',
                        border: '1px solid #ddd'
                      }}>
                        <h5 style={{ margin: '0 0 10px 0' }}>Información del Recurso</h5>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                          <div>
                            <label style={{ fontSize: '12px', color: '#666' }}>Cantidad Presupuestada</label>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: 'bold' }}>
                              {parseFloat(recursoSeleccionado.cantidad_presupuesto).toLocaleString('es-CL')}
                            </p>
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#666' }}>Cantidad Solicitada</label>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: 'bold' }}>
                              {parseFloat(recursoSeleccionado.cantidad_solicitada).toLocaleString('es-CL')}
                            </p>
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#666' }}>Cantidad Disponible</label>
                            <p style={{
                              margin: '5px 0 0 0',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              color: cantidadDisponible < 0 ? '#f44336' : '#4CAF50'
                            }}>
                              {parseFloat(cantidadDisponible).toLocaleString('es-CL')}
                            </p>
                          </div>
                          <div>
                            <label style={{ fontSize: '12px', color: '#666' }}>Precio Unitario (Presupuesto)</label>
                            <p style={{ margin: '5px 0 0 0', fontSize: '14px', fontWeight: 'bold' }}>
                              ${parseFloat(recursoSeleccionado.precio_unitario_costo).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Formulario de Entrada */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                    <div>
                      <label>Cantidad a Solicitar *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={nuevoRecurso.cantidad}
                        onChange={(e) => setNuevoRecurso(prev => ({ ...prev, cantidad: e.target.value }))}
                        style={{ width: '100%', padding: '8px' }}
                      />
                    </div>
                    <div>
                      <label>Precio Unitario *</label>
                      <input
                        type="number"
                        step="0.01"
                        value={nuevoRecurso.precio_unitario}
                        onChange={(e) => setNuevoRecurso(prev => ({ ...prev, precio_unitario: e.target.value }))}
                        style={{ width: '100%', padding: '8px' }}
                      />
                    </div>
                  </div>

                  {/* Botones de Acción */}
                  <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                      type="button"
                      className="btn-primary"
                      onClick={handleAgregarRecurso}
                      style={{ flex: 1, padding: '8px' }}
                    >
                      Agregar
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => setMostrarAgregarRecurso(false)}
                      style={{ flex: 1, padding: '8px' }}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setMostrarAgregarRecurso(true)}
                  >
                    + Agregar Recurso
                  </button>
                </div>
              )}

              <div style={{
                textAlign: 'right',
                marginTop: '15px',
                paddingTop: '16px',
                borderTop: '3px solid #69B32D',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <p style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#1f2937',
                  margin: 0
                }}>
                  <span style={{ marginRight: '10px' }}>TOTAL:</span>
                  <span style={{ color: '#ef4444' }}>
                    ${calcularTotal().toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                  </span>
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-secondary" onClick={() => {
                setEditMode(false);
                cargarDatos();
              }}>
                Cancelar
              </button>
              <button type="submit" className="btn-primary" disabled={loading}>
                <FiSave /> Guardar Cambios
              </button>
            </div>
          </form>
        ) : (
          <div className="modal-view">
            <div className="view-section" style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Proveedor:</label>
                  <p>{proveedores.find(p => p.id === formData.proveedor)?.nombre || 'No especificado'}</p>
                </div>

                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Fecha de Emisión:</label>
                  <p>{formatDate(formData.fecha_emision)}</p>
                </div>

                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Estado:</label>
                  <p>{formData.estado}</p>
                </div>

                <div>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Tipo de Documento:</label>
                  <p>{formData.tipo_documento || 'No especificado'}</p>
                </div>
              </div>

              {formData.descripcion && (
                <div style={{ marginTop: '15px' }}>
                  <label style={{ fontWeight: 'bold', color: '#666' }}>Descripción:</label>
                  <p>{formData.descripcion}</p>
                </div>
              )}
            </div>

            <div className="view-section" style={{ padding: '20px' }}>
              <h3>Recursos de la Orden</h3>

              <table className="detalles-table" style={{
                width: '100%',
                borderCollapse: 'collapse',
                backgroundColor: 'white',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}>
                <thead>
                  <tr style={{
                    background: 'linear-gradient(135deg, #102826 0%, #69B32D 100%)',
                    color: 'white'
                  }}>
                    <th style={{ textAlign: 'left', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Recurso</th>
                    <th style={{ textAlign: 'center', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Cantidad</th>
                    <th style={{ textAlign: 'right', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Precio Unitario</th>
                    <th style={{ textAlign: 'right', padding: '16px', fontWeight: 600, fontSize: '0.95rem' }}>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {detalles.map((detalle, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <td style={{ padding: '14px', fontSize: '0.9rem' }}>
                        <div style={{ fontWeight: 600, color: '#1f2937' }}>
                          {detalle.recurso_data?.nombre || detalle.descripcion}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                        {parseFloat(detalle.cantidad).toLocaleString('es-CL')}
                      </td>
                      <td style={{ textAlign: 'right', padding: '14px', fontSize: '0.9rem', color: '#374151' }}>
                        ${parseFloat(detalle.precio_unitario).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                      </td>
                      <td style={{
                        textAlign: 'right',
                        padding: '14px',
                        fontSize: '0.95rem',
                        fontWeight: 700,
                        color: '#ef4444'
                      }}>
                        ${(parseFloat(detalle.cantidad) * parseFloat(detalle.precio_unitario)).toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{
                textAlign: 'right',
                marginTop: '15px',
                paddingTop: '16px',
                borderTop: '3px solid #69B32D',
                background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                padding: '16px',
                borderRadius: '8px'
              }}>
                <p style={{
                  fontSize: '1.2rem',
                  fontWeight: 700,
                  color: '#1f2937',
                  margin: 0
                }}>
                  <span style={{ marginRight: '10px' }}>TOTAL:</span>
                  <span style={{ color: '#ef4444' }}>
                    ${calcularTotal().toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                  </span>
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn-primary" onClick={() => setEditMode(true)}>
                <FiEdit2 /> Editar
              </button>
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default VerEditarOrdenCompraModal;
